import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CarFront,
  ClipboardList,
  Droplets,
  FileBarChart2,
  BarChart3,
  Fuel,
  Navigation,
  Receipt,
  Calculator,
  ChevronLeft,
  ChevronRight,
  X,
} from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import SectionTitle from './ui/SectionTitle';

interface ScreenData {
  icon: React.ComponentType<LucideProps>;
  iconColor: string;
  iconBg: string;
  title: string;
  description: string;
  imageSrc: string;
  imageAlt: string;
}

// Ordem de prioridade: 1º Calculadora Flex, 2º Novo Abastecimento, 3º Relatório Anual
const screens: ScreenData[] = [
  {
    icon: Fuel,
    iconColor: 'text-amber-500',
    iconBg: 'bg-amber-50',
    title: 'Calculadora Flex',
    description: 'Etanol × gasolina baseada no consumo real — escolha sempre a opção mais vantajosa.',
    imageSrc: '/screenshots/tela-flex-calc-1.webp',
    imageAlt: 'Calculadora flex etanol vs gasolina no Gas2',
  },
  {
    icon: Droplets,
    iconColor: 'text-blue-500',
    iconBg: 'bg-blue-50',
    title: 'Novo abastecimento',
    description: 'Registro rápido com litros, preço por litro, odômetro e foto do cupom fiscal.',
    imageSrc: '/screenshots/tela-new-supply.webp',
    imageAlt: 'Tela de novo abastecimento no Gas2',
  },
  {
    icon: BarChart3,
    iconColor: 'text-teal-500',
    iconBg: 'bg-teal-50',
    title: 'Relatório anual',
    description: 'Visão consolidada do ano: gastos, litros e evolução mês a mês.',
    imageSrc: '/screenshots/tela-supply-report-yearly.webp',
    imageAlt: 'Relatório anual de abastecimentos no Gas2',
  },
  {
    icon: Calculator,
    iconColor: 'text-indigo-500',
    iconBg: 'bg-indigo-50',
    title: 'Estimativa de custo',
    description: 'Simulação de gasto para qualquer distância com seu carro.',
    imageSrc: '/screenshots/tela-flex-calc-2.webp',
    imageAlt: 'Calculadora de estimativa de custo no Gas2',
  },
  {
    icon: Receipt,
    iconColor: 'text-slate-500',
    iconBg: 'bg-slate-50',
    title: 'Histórico de abastecimentos',
    description: 'Lista completa de todos os abastecimentos com filtros por veículo e período.',
    imageSrc: '/screenshots/tela-supplies.webp',
    imageAlt: 'Histórico de abastecimentos no Gas2',
  },
  {
    icon: CarFront,
    iconColor: 'text-violet-500',
    iconBg: 'bg-violet-50',
    title: 'Garagem',
    description: 'Seus veículos com consumo médio, histórico e dados completos.',
    imageSrc: '/screenshots/tela-garage-list.webp',
    imageAlt: 'Lista de veículos na garagem do Gas2',
  },
  {
    icon: FileBarChart2,
    iconColor: 'text-emerald-500',
    iconBg: 'bg-emerald-50',
    title: 'Relatório de abastecimentos',
    description: 'Total de litros, custo do período e posto mais frequente.',
    imageSrc: '/screenshots/tela-supply-report.webp',
    imageAlt: 'Relatório de abastecimentos no Gas2',
  },
  {
    icon: ClipboardList,
    iconColor: 'text-rose-500',
    iconBg: 'bg-rose-50',
    title: 'Checklist mensal',
    description: 'Lista de verificação periódica para manter seu veículo sempre em dia.',
    imageSrc: '/screenshots/tela-jmonthly-checklist.webp',
    imageAlt: 'Checklist mensal de manutenção no Gas2',
  },
  {
    icon: Navigation,
    iconColor: 'text-orange-500',
    iconBg: 'bg-orange-50',
    title: 'Jornada',
    description: 'Acompanhe viagens e trajetos com custo estimado em tempo real.',
    imageSrc: '/screenshots/tela-juorney.webp',
    imageAlt: 'Tela de jornada/viagem no Gas2',
  },
];

// ------- Lightbox -------
interface LightboxProps {
  screens: ScreenData[];
  index: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}

const Lightbox: React.FC<LightboxProps> = ({ screens, index, onClose, onPrev, onNext }) => {
  const current = screens[index];

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, onPrev, onNext]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <motion.div
      key="lightbox-backdrop"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 px-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
        aria-label="Fechar"
      >
        <X className="h-5 w-5" strokeWidth={2} />
      </button>

      <button
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        className="absolute left-3 sm:left-6 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
        aria-label="Anterior"
      >
        <ChevronLeft className="h-6 w-6" strokeWidth={2} />
      </button>

      <motion.div
        key={current.imageSrc}
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.92 }}
        transition={{ duration: 0.2 }}
        className="relative flex max-h-[90vh] flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-[260px] sm:w-[300px] rounded-[2.6rem] bg-gray-950 p-[10px] shadow-2xl ring-1 ring-white/15">
          <div className="overflow-hidden rounded-[2.2rem]" style={{ height: 540 }}>
            <img
              src={current.imageSrc}
              alt={current.imageAlt}
              className="h-full w-full object-cover object-top"
            />
          </div>
        </div>
        <div className="mt-5 text-center">
          <p className="text-sm font-semibold text-white">{current.title}</p>
          <p className="mt-1 text-xs text-white/60">{current.description}</p>
        </div>
        <div className="mt-4 flex gap-1.5">
          {screens.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? 'w-5 bg-amber-400' : 'w-1.5 bg-white/30'
              }`}
            />
          ))}
        </div>
      </motion.div>

      <button
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        className="absolute right-3 sm:right-6 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
        aria-label="Próximo"
      >
        <ChevronRight className="h-6 w-6" strokeWidth={2} />
      </button>
    </motion.div>
  );
};

// ------- Carousel Card -------
interface CarouselCardProps extends ScreenData {
  onClick: () => void;
  index: number;
}

const CarouselCard: React.FC<CarouselCardProps> = ({
  icon: Icon,
  iconColor,
  iconBg,
  title,
  description,
  imageSrc,
  imageAlt,
  onClick,
  index,
}) => (
  <motion.article
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.45, delay: Math.min(index * 0.07, 0.35) }}
    className="group flex-shrink-0 w-[220px] sm:w-[240px] overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm
      transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl snap-start"
  >
    {/* Preview com frame de celular — clicável */}
    <div
      className="relative flex h-[360px] cursor-zoom-in items-center justify-center overflow-hidden bg-gray-900"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      aria-label={`Ver ${title} em tela cheia`}
    >
      <div className="w-[152px] rounded-[2rem] bg-gray-950 p-[7px] shadow-2xl ring-1 ring-white/10">
        <div className="overflow-hidden rounded-[1.6rem]" style={{ height: 306 }}>
          <img
            src={imageSrc}
            alt={imageAlt}
            className="h-full w-full object-cover object-top"
            loading={index < 3 ? 'eager' : 'lazy'}
          />
        </div>
      </div>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 to-transparent transition-opacity duration-200 group-hover:opacity-70" />
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-200 group-hover:opacity-100">
        <span className="rounded-full bg-white/20 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur-sm">
          Ver maior
        </span>
      </div>
    </div>

    {/* Rodapé do card */}
    <div className="p-4">
      <div className={`mb-2 inline-flex h-9 w-9 items-center justify-center rounded-xl ${iconBg}`}>
        <Icon className={`h-5 w-5 ${iconColor}`} strokeWidth={1.5} />
      </div>
      <h3 className="text-sm font-bold text-gray-900">{title}</h3>
      <p className="mt-1 text-xs leading-relaxed text-gray-500">{description}</p>
    </div>
  </motion.article>
);

// ------- Seção principal -------
const Screenshots: React.FC = () => {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const close = useCallback(() => setLightboxIndex(null), []);
  const prev = useCallback(() =>
    setLightboxIndex((i) => (i === null ? null : (i - 1 + screens.length) % screens.length)), []);
  const next = useCallback(() =>
    setLightboxIndex((i) => (i === null ? null : (i + 1) % screens.length)), []);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 8);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 8);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    return () => el.removeEventListener('scroll', updateScrollState);
  }, [updateScrollState]);

  const scrollBy = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === 'left' ? -280 : 280, behavior: 'smooth' });
  };

  return (
    <section id="screenshots" className="bg-gray-50 py-20 sm:py-28 overflow-hidden">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionTitle
          eyebrow="Telas do app"
          title="Veja o Gas2 em ação"
          subtitle="Uma visão rápida das telas essenciais — de abastecimentos a relatórios, tudo na palma da mão."
        />
      </div>

      {/* Carousel container com fade nas bordas */}
      <div className="relative mt-14">
        {/* Fade esquerdo */}
        <div
          className={`pointer-events-none absolute left-0 top-0 z-10 h-full w-20 bg-gradient-to-r from-gray-50 to-transparent transition-opacity duration-300 ${
            canScrollLeft ? 'opacity-100' : 'opacity-0'
          }`}
        />
        {/* Fade direito */}
        <div
          className={`pointer-events-none absolute right-0 top-0 z-10 h-full w-20 bg-gradient-to-l from-gray-50 to-transparent transition-opacity duration-300 ${
            canScrollRight ? 'opacity-100' : 'opacity-0'
          }`}
        />

        {/* Botão scroll ← */}
        <button
          onClick={() => scrollBy('left')}
          disabled={!canScrollLeft}
          className={`absolute left-3 sm:left-6 top-1/2 z-20 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-lg ring-1 ring-gray-200 text-gray-700 transition hover:bg-amber-50 hover:text-amber-600 hover:ring-amber-300 disabled:opacity-0 disabled:pointer-events-none`}
          aria-label="Rolar para a esquerda"
        >
          <ChevronLeft className="h-5 w-5" strokeWidth={2} />
        </button>

        {/* Botão scroll → */}
        <button
          onClick={() => scrollBy('right')}
          disabled={!canScrollRight}
          className={`absolute right-3 sm:right-6 top-1/2 z-20 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-lg ring-1 ring-gray-200 text-gray-700 transition hover:bg-amber-50 hover:text-amber-600 hover:ring-amber-300 disabled:opacity-0 disabled:pointer-events-none`}
          aria-label="Rolar para a direita"
        >
          <ChevronRight className="h-5 w-5" strokeWidth={2} />
        </button>

        {/* Scroll track */}
        <div
          ref={scrollRef}
          className="flex gap-5 overflow-x-auto scroll-smooth pb-6 pt-2 px-4 sm:px-8 lg:px-[max(2rem,calc((100vw-80rem)/2+2rem))]"
          style={{ scrollSnapType: 'x mandatory', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {screens.map((s, i) => (
            <CarouselCard key={s.title} {...s} index={i} onClick={() => setLightboxIndex(i)} />
          ))}
          {/* Spacer para padding-right visual */}
          <div className="flex-shrink-0 w-4 sm:w-8" aria-hidden="true" />
        </div>
      </div>

      <AnimatePresence>
        {lightboxIndex !== null && (
          <Lightbox
            screens={screens}
            index={lightboxIndex}
            onClose={close}
            onPrev={prev}
            onNext={next}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

export default Screenshots;

